import { getBankAccountTransactions, useBranches } from "@api";
import { useUser } from "@auth";
import { ClientTable, Badge, PageHeader, PageTitle } from "@components";
import { formatAmount, formatDateTime, formatDateLong } from "@utils";
import { useEffect, useState, useMemo } from "react";
import { Stripe, Bank, CreditCard2BackFill } from "react-bootstrap-icons";
import { payoutStatusColors } from "../common";
import PayoutDetails from "../displays/PayoutDetails";
import { TransactionDetails } from "@areas";

const Payouts = () => {
  const { user } = useUser();
  const [payouts, setPayouts] = useState(null);
  const [selectedTransaction, setSelectedTransaction] = useState(null);
  const [payoutId, setPayoutId] = useState(null);
  const { data: branches = [] } = useBranches();
  const memoizedBranches = useMemo(
    () => branches.map((branch) => branch.name),
    [branches],
  );

  useEffect(() => {
    async function fetchData() {
      const data = await getBankAccountTransactions();
      setPayouts(data);
    }

    fetchData();
  }, []);

  const reloadData = async () => {
    const data = await getBankAccountTransactions();
    setPayouts(data);
  };

  const tableConfig = useMemo(
    () => ({
      key: "payoutTransactions",
      defaultSortDirection: "desc",
      defaultSortKey: "settlementDate",
      onClick: (x) =>
        x.payoutId ? setPayoutId(x.payoutId) : setSelectedTransaction(x),
      search: (x, s) =>
        (x.customerName ?? "").toUpperCase().includes(s.toUpperCase()) ||
        (x.reference ?? "").toUpperCase().includes(s.toUpperCase()) ||
        `${x.amount}`.includes(s),
      colCustomisation: true,
      columns: [
        {
          id: "amount",
          title: "Amount",
          key: "amount",
          width: "120px",
          render: (x) => formatAmount(x.amount),
          right: true,
          sortFunc: (a, b) => a.amount - b.amount,
        },
        {
          title: "Transaction Date",
          key: "transactionDate",
          id: "transactionDate",
          width: "160px",
          render: (x) => formatDateTime(x.transactionDate),
          type: "date",
          sortFunc: (a, b) =>
            new Date(a.transactionDate) - new Date(b.transactionDate),
        },
        {
          title: "Settlement Date",
          key: "settlementDate",
          id: "settlementDate",
          width: "160px",
          type: "date",
          render: (x) => formatDateLong(x.settlementDate),
          sortFunc: (a, b, direction) => {
            // display records with empty settlement dates on top by default
            const dateA = a.settlementDate ? new Date(a.settlementDate) : null;
            const dateB = b.settlementDate ? new Date(b.settlementDate) : null;

            if (!dateA && !dateB) return 0;
            if (!dateA) return direction === "desc" ? -1 : 1;
            if (!dateB) return direction === "desc" ? 1 : -1;

            return dateA - dateB;
          },
        },
        {
          title: "Customer Name",
          key: "customerName",
          id: "customerName",
          width: "200px",
          type: "string",
          render: (x) => x.customerName,
          sortFunc: (a, b) =>
            (a.customerName || "").localeCompare(b.customerName),
        },
        {
          title: "Status",
          key: "status",
          id: "status",
          width: "120px",
          type: "enum",
          values: ["Paid", "Failed", "In-transit", "Refunded"],
          render: (x) => (
            <Badge color={payoutStatusColors[x.status]}>{x.status}</Badge>
          ),
        },
        {
          title: "Reference",
          key: "reference",
          id: "reference",
          width: "200px",
          type: "string",
          render: (x) => x.reference,
          sortFunc: (a, b) => (a.reference || "").localeCompare(b.reference),
        },
        {
          title: "Branch",
          key: "branch",
          id: "branch",
          width: "120px",
          render: (x) => x.branch,
          type: "enum",
          values: memoizedBranches,
        },
        {
          title: "Type",
          key: "type",
          id: "type",
          width: "150px",
          render: (x) => {
            const type = x.type;
            let icon = null;

            if (type === "Stripe") {
              icon = <Stripe className="block text-indigo-600" />;
            } else if (type === "Adyen") {
              icon = <CreditCard2BackFill className="block text-teal-500" />;
            } else {
              icon = <Bank className="inline text-gray-600" />;
            }

            return (
              <div className="flex items-center gap-2">
                {icon}
                <p className="inline">{type}</p>
              </div>
            );
          },
          type: "enum",
          values: ["Stripe", "Adyen", "Pay by Bank", "Pay by Bank refund"],
        },
      ],
    }),
    [memoizedBranches],
  );

  return (
    <>
      <PageHeader>
        <PageTitle>Payouts</PageTitle>
      </PageHeader>
      <ClientTable config={tableConfig} data={payouts} />
      <PayoutDetails id={payoutId} onClose={() => setPayoutId(null)} />
      <TransactionDetails
        onClose={() => setSelectedTransaction(null)}
        id={selectedTransaction && selectedTransaction.paymentId}
        paymentMethodKey="paybybank"
        onChange={() => reloadData()}
      />
    </>
  );
};

export default Payouts;
